/**
 * PortalABTesting — A/B Testing for Entry Verticals
 *
 * Users create A/B tests to compare which entry vertical performs better
 * for a given traffic source (utm_source). Metrics are pulled from
 * session_events to show sessions, conversions, CVR%, and RPUS per variant.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { supabaseGet } from '@/utils/supabaseRest';
import {
  FlaskConical, Plus, X, Play, Pause, Trophy, Loader2, AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// ─── Types ───────────────────────────────────────

interface ABTest {
  id: string;
  name: string;
  utm_source: string;
  variant_a_vertical: string;
  variant_b_vertical: string;
  split_pct: number; // 0-100, percentage going to variant A
  status: 'active' | 'paused';
  created_at: string;
}

interface RawEvent {
  session_id: string;
  event_type: string;
  vertical_id: string | null;
  revenue: number | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

interface VerticalRef {
  id: string;
  name: string;
}

interface VariantMetrics {
  sessions: number;
  conversions: number;
  cvr: number;
  rpus: number;
}

// ─── LocalStorage helpers ────────────────────────

const LS_KEY = 'intentdrive_ab_tests';

function loadTests(): ABTest[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveTests(tests: ABTest[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(tests));
}

// ─── Component ───────────────────────────────────

export default function PortalABTesting() {
  const [tests, setTests] = useState<ABTest[]>(loadTests);
  const [events, setEvents] = useState<RawEvent[]>([]);
  const [verticals, setVerticals] = useState<VerticalRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [formName, setFormName] = useState('');
  const [formUtm, setFormUtm] = useState('');
  const [formA, setFormA] = useState('');
  const [formB, setFormB] = useState('');
  const [formSplit, setFormSplit] = useState(50);

  // Fetch events + verticals
  useEffect(() => {
    (async () => {
      try {
        const since = new Date(Date.now() - 90 * 86400000).toISOString();
        const [evts, verts] = await Promise.all([
          supabaseGet<RawEvent[]>(`session_events?select=session_id,event_type,vertical_id,revenue,metadata,created_at&order=created_at.desc&limit=2000&created_at=gte.${since}`),
          supabaseGet<VerticalRef[]>('verticals?select=id,name'),
        ]);
        setEvents(evts);
        setVerticals(verts);
      } catch (e) {
        console.error('Failed to load A/B test data:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const verticalMap = useMemo(() => {
    const m: Record<string, string> = {};
    verticals.forEach(v => { m[v.id] = v.name; });
    return m;
  }, [verticals]);

  // Compute metrics for a test
  const computeMetrics = useCallback((test: ABTest): { a: VariantMetrics; b: VariantMetrics } => {
    const sessionsA = new Set<string>();
    const sessionsB = new Set<string>();
    let convA = 0, convB = 0, revA = 0, revB = 0;

    for (const e of events) {
      if (e.vertical_id === test.variant_a_vertical) {
        sessionsA.add(e.session_id);
        if (e.event_type === 'converted') { convA++; revA += e.revenue || 0; }
      }
      if (e.vertical_id === test.variant_b_vertical) {
        sessionsB.add(e.session_id);
        if (e.event_type === 'converted') { convB++; revB += e.revenue || 0; }
      }
    }

    const sA = sessionsA.size;
    const sB = sessionsB.size;
    return {
      a: { sessions: sA, conversions: convA, cvr: sA ? (convA / sA) * 100 : 0, rpus: sA ? revA / sA : 0 },
      b: { sessions: sB, conversions: convB, cvr: sB ? (convB / sB) * 100 : 0, rpus: sB ? revB / sB : 0 },
    };
  }, [events]);

  const createTest = () => {
    if (!formName || !formA || !formB) return;
    const newTest: ABTest = {
      id: crypto.randomUUID(),
      name: formName,
      utm_source: formUtm,
      variant_a_vertical: formA,
      variant_b_vertical: formB,
      split_pct: formSplit,
      status: 'active',
      created_at: new Date().toISOString(),
    };
    const updated = [...tests, newTest];
    setTests(updated);
    saveTests(updated);
    setShowForm(false);
    setFormName(''); setFormUtm(''); setFormA(''); setFormB(''); setFormSplit(50);
  };

  const toggleStatus = (id: string) => {
    const updated = tests.map(t =>
      t.id === id ? { ...t, status: t.status === 'active' ? 'paused' as const : 'active' as const } : t
    );
    setTests(updated);
    saveTests(updated);
  };

  const deleteTest = (id: string) => {
    const updated = tests.filter(t => t.id !== id);
    setTests(updated);
    saveTests(updated);
  };

  const getSignificance = (a: VariantMetrics, b: VariantMetrics): { label: string; color: string } => {
    if (a.sessions < 30 || b.sessions < 30) return { label: 'Need more data', color: 'text-amber-400' };
    const diff = Math.abs(a.cvr - b.cvr);
    const avg = (a.cvr + b.cvr) / 2;
    if (avg === 0) return { label: 'Need more data', color: 'text-amber-400' };
    if ((diff / avg) * 100 > 10) return { label: 'Significant', color: 'text-emerald-400' };
    return { label: 'Not significant', color: 'text-slate-400' };
  };

  return (
    <PortalLayout>
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FlaskConical className="h-6 w-6 text-emerald-400" />
            <h1 className="text-2xl font-bold">A/B Tests</h1>
          </div>
          <Button
            onClick={() => setShowForm(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <Plus className="h-4 w-4 mr-2" /> Create Test
          </Button>
        </div>

        {/* Create Test Dialog */}
        {showForm && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
            <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 w-full max-w-lg space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">Create A/B Test</h2>
                <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-white">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Test Name</label>
                  <input
                    value={formName}
                    onChange={e => setFormName(e.target.value)}
                    placeholder="e.g. Facebook traffic entry vertical test"
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Traffic Source Filter (utm_source)</label>
                  <input
                    value={formUtm}
                    onChange={e => setFormUtm(e.target.value)}
                    placeholder="e.g. facebook (optional)"
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Variant A Vertical</label>
                    <select
                      value={formA}
                      onChange={e => setFormA(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    >
                      <option value="">Select vertical...</option>
                      {verticals.map(v => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Variant B Vertical</label>
                    <select
                      value={formB}
                      onChange={e => setFormB(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    >
                      <option value="">Select vertical...</option>
                      {verticals.map(v => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">
                    Traffic Split: {formSplit}% A / {100 - formSplit}% B
                  </label>
                  <input
                    type="range"
                    min={10}
                    max={90}
                    value={formSplit}
                    onChange={e => setFormSplit(Number(e.target.value))}
                    className="w-full accent-emerald-500"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setShowForm(false)} className="text-slate-400">
                  Cancel
                </Button>
                <Button
                  onClick={createTest}
                  disabled={!formName || !formA || !formB}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  Create Test
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
            <span className="ml-2 text-slate-400">Loading test data...</span>
          </div>
        )}

        {/* Tests Table */}
        {!loading && tests.length === 0 && (
          <div className="text-center py-20 text-slate-500">
            <FlaskConical className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No A/B tests yet. Create one to start comparing entry verticals.</p>
          </div>
        )}

        {!loading && tests.map(test => {
          const { a, b } = computeMetrics(test);
          const sig = getSignificance(a, b);
          const winnerA = a.cvr > b.cvr && a.sessions >= 30;
          const winnerB = b.cvr > a.cvr && b.sessions >= 30;

          return (
            <div key={test.id} className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-4">
              {/* Test header */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">{test.name}</h3>
                  <p className="text-sm text-slate-500">
                    {test.utm_source ? `utm_source: ${test.utm_source}` : 'All traffic'} &middot; Split: {test.split_pct}/{100 - test.split_pct}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${test.status === 'active' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-slate-700 text-slate-400'}`}>
                    {test.status}
                  </span>
                  <Button variant="ghost" size="icon" onClick={() => toggleStatus(test.id)} className="text-slate-400 hover:text-white">
                    {test.status === 'active' ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => deleteTest(test.id)} className="text-slate-400 hover:text-red-400">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Metrics comparison */}
              <div className="grid grid-cols-2 gap-4">
                {/* Variant A */}
                <div className={`rounded-lg p-4 border ${winnerA ? 'border-emerald-500 bg-emerald-500/5' : 'border-slate-700 bg-slate-800/50'}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-sm font-semibold text-blue-400">Variant A</span>
                    <span className="text-xs text-slate-500">{verticalMap[test.variant_a_vertical] || test.variant_a_vertical}</span>
                    {winnerA && <Trophy className="h-4 w-4 text-emerald-400" />}
                  </div>
                  <div className="grid grid-cols-4 gap-3 text-center">
                    <div>
                      <p className="text-xs text-slate-500">Sessions</p>
                      <p className="text-lg font-bold">{a.sessions}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Conversions</p>
                      <p className="text-lg font-bold">{a.conversions}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">CVR%</p>
                      <p className="text-lg font-bold">{a.cvr.toFixed(1)}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">RPUS</p>
                      <p className="text-lg font-bold">${a.rpus.toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                {/* Variant B */}
                <div className={`rounded-lg p-4 border ${winnerB ? 'border-emerald-500 bg-emerald-500/5' : 'border-slate-700 bg-slate-800/50'}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-sm font-semibold text-purple-400">Variant B</span>
                    <span className="text-xs text-slate-500">{verticalMap[test.variant_b_vertical] || test.variant_b_vertical}</span>
                    {winnerB && <Trophy className="h-4 w-4 text-emerald-400" />}
                  </div>
                  <div className="grid grid-cols-4 gap-3 text-center">
                    <div>
                      <p className="text-xs text-slate-500">Sessions</p>
                      <p className="text-lg font-bold">{b.sessions}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Conversions</p>
                      <p className="text-lg font-bold">{b.conversions}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">CVR%</p>
                      <p className="text-lg font-bold">{b.cvr.toFixed(1)}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">RPUS</p>
                      <p className="text-lg font-bold">${b.rpus.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Significance */}
              <div className="flex items-center gap-2 text-sm">
                <AlertCircle className="h-4 w-4 text-slate-500" />
                <span className="text-slate-500">Statistical significance:</span>
                <span className={sig.color}>{sig.label}</span>
              </div>
            </div>
          );
        })}
      </div>
    </PortalLayout>
  );
}
